// Pure ability math (no Phaser at runtime): ultimate charge rules, signature
// cooldowns, swept-box marking for Arc Dash, chain ordering, Siege Field
// placement and small geometry helpers. Unit-tested in tests/abilities.test.ts.

import type { DamageInfo } from '../../core/types';

/** Axis-aligned rectangle, (x, y) = top-left corner (same convention as HitArea and zone rects). */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Point {
  x: number;
  y: number;
}

const EPS = 1e-9;

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Wrap an angle to [-π, π). */
export function wrapAngle(a: number): number {
  const tau = Math.PI * 2;
  let r = (a + Math.PI) % tau;
  if (r < 0) r += tau;
  return r - Math.PI;
}

// ───────────────────────────── Ultimate charge ─────────────────────────────

/** Charge rules (points; ULTIMATE_COST is the full bar). */
export const CHARGE = {
  /** Points per point of direct player damage dealt. */
  perDamageDealt: 1 / 8,
  kill: 4,
  eliteKill: 12,
  bossKill: 25,
  /** Points per point of damage taken. */
  perDamageTaken: 1 / 5,
} as const;

/** Charge for an enemy:damaged event: only direct player damage (no echoes / secondary effects, no DoT ticks). */
export function chargeForDamageDealt(amount: number, info: Pick<DamageInfo, 'source' | 'secondary' | 'isDot'>): number {
  if (info.source !== 'player' || info.secondary || info.isDot) return 0;
  return Math.max(0, amount) * CHARGE.perDamageDealt;
}

/** Charge for a kill by the player. Props (crates) never charge. */
export function chargeForKill(kind: 'enemy' | 'boss' | 'prop', elite: boolean, source: DamageInfo['source']): number {
  if (source !== 'player' || kind === 'prop') return 0;
  if (kind === 'boss') return CHARGE.bossKill;
  return elite ? CHARGE.eliteKill : CHARGE.kill;
}

/** Charge for damage the player took (hp + shield). Self-inflicted costs (FEEDBACK_LOOP) never charge. */
export function chargeForDamageTaken(amount: number, source: DamageInfo['source']): number {
  if (source === 'self') return 0;
  return Math.max(0, amount) * CHARGE.perDamageTaken;
}

/** Add raw points × the ultimateCharge stat, clamped to [0, cost]. */
export function addChargePoints(current: number, points: number, statMult: number, cost: number): number {
  if (!(points > 0) || !(statMult > 0)) return clamp(current, 0, cost);
  return clamp(current + points * statMult, 0, cost);
}

// ───────────────────────────── Signature ─────────────────────────────

/** Final signature cooldown in seconds (stat multiplier clamped so it never reaches 0). */
export function signatureCooldownSeconds(base: number, statMult: number): number {
  return Math.max(0.5, base * Math.max(0.1, statMult));
}

/** FEEDBACK_LOOP: HP cost of a signature use. Never takes the player below 1 HP. */
export const FEEDBACK_COST = 8;
export function feedbackCost(hp: number): number {
  return hp > FEEDBACK_COST + 1 ? FEEDBACK_COST : 0;
}

// ───────────────────────────── Stillness (Bulwark Frame / Walking Fortress) ─────────────────────────────

/** Tracks how long the mech has been (nearly) still on the ground. */
export class StillTracker {
  /** Seconds the mech has been still. */
  stillFor = 0;
  constructor(private readonly maxSpeed = 30) {}

  update(dt: number, vx: number, onGround: boolean, alive = true): void {
    if (alive && onGround && Math.abs(vx) < this.maxSpeed) this.stillFor += dt;
    else this.stillFor = 0;
  }

  isStill(seconds: number): boolean {
    return this.stillFor >= seconds;
  }

  reset(): void {
    this.stillFor = 0;
  }
}

// ───────────────────────────── Swept marking (Arc Dash) ─────────────────────────────

/**
 * Swept box vs rect: does a box of half extents (hw, hh) whose centre moves from
 * (x0, y0) to (x1, y1) touch `r`? Equivalent to the centre segment vs `r` grown by
 * the half extents (Minkowski sum). Returns the entry t ∈ [0, 1] (0 when already
 * overlapping at the start), or -1 on a miss.
 */
export function sweptBoxEntry(x0: number, y0: number, x1: number, y1: number, hw: number, hh: number, r: Rect): number {
  const minX = r.x - hw;
  const minY = r.y - hh;
  const maxX = r.x + r.w + hw;
  const maxY = r.y + r.h + hh;
  const dx = x1 - x0;
  const dy = y1 - y0;
  let tMin = 0;
  let tMax = 1;
  if (Math.abs(dx) < EPS) {
    if (x0 < minX || x0 > maxX) return -1;
  } else {
    let t1 = (minX - x0) / dx;
    let t2 = (maxX - x0) / dx;
    if (t1 > t2) [t1, t2] = [t2, t1];
    tMin = Math.max(tMin, t1);
    tMax = Math.min(tMax, t2);
    if (tMin > tMax) return -1;
  }
  if (Math.abs(dy) < EPS) {
    if (y0 < minY || y0 > maxY) return -1;
  } else {
    let t1 = (minY - y0) / dy;
    let t2 = (maxY - y0) / dy;
    if (t1 > t2) [t1, t2] = [t2, t1];
    tMin = Math.max(tMin, t1);
    tMax = Math.min(tMax, t2);
    if (tMin > tMax) return -1;
  }
  return tMin;
}

export interface MarkCandidate<T> {
  item: T;
  areas: readonly Rect[];
}

/**
 * Candidates touched by the swept box this step, ordered by the moment they were
 * first touched (path order). Items in `skip` (already marked) are ignored.
 */
export function sweepMarks<T>(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  hw: number,
  hh: number,
  candidates: readonly MarkCandidate<T>[],
  skip?: { has(item: T): boolean },
): T[] {
  const hits: { item: T; t: number }[] = [];
  for (const c of candidates) {
    if (skip?.has(c.item)) continue;
    let best = Infinity;
    for (const a of c.areas) {
      const t = sweptBoxEntry(x0, y0, x1, y1, hw, hh, a);
      if (t >= 0 && t < best) best = t;
    }
    if (best !== Infinity) hits.push({ item: c.item, t: best });
  }
  hits.sort((a, b) => a.t - b.t);
  return hits.map((h) => h.item);
}

/** Candidates touched anywhere along a polyline path (in path order). */
export function sweepPath<T>(path: readonly Point[], hw: number, hh: number, candidates: readonly MarkCandidate<T>[]): T[] {
  const found = new Set<T>();
  const out: T[] = [];
  if (path.length === 1) {
    for (const t of sweepMarks(path[0].x, path[0].y, path[0].x, path[0].y, hw, hh, candidates)) {
      found.add(t);
      out.push(t);
    }
    return out;
  }
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1];
    const b = path[i];
    for (const t of sweepMarks(a.x, a.y, b.x, b.y, hw, hh, candidates, found)) {
      found.add(t);
      out.push(t);
    }
  }
  return out;
}

/** Greedy nearest-neighbour visiting order starting at `from` (Overload arcs). Returns indices. */
export function nearestNeighbourOrder(from: Point, points: readonly Point[]): number[] {
  const n = points.length;
  const used = new Array<boolean>(n).fill(false);
  const order: number[] = [];
  let cx = from.x;
  let cy = from.y;
  for (let k = 0; k < n; k++) {
    let best = -1;
    let bestD = Infinity;
    for (let i = 0; i < n; i++) {
      if (used[i]) continue;
      const d = (points[i].x - cx) ** 2 + (points[i].y - cy) ** 2;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    used[best] = true;
    order.push(best);
    cx = points[best].x;
    cy = points[best].y;
  }
  return order;
}

/** Number of extra chain jumps after the marked targets (Arc Dash). */
export function arcDashExtraChains(chainBonus: number, evolved: boolean): number {
  return Math.max(0, 2 + Math.floor(chainBonus) + (evolved ? 2 : 0));
}

/** Distance from a point to the edge of a circle (0 when inside). */
export function edgeDistance(px: number, py: number, cx: number, cy: number, r: number): number {
  return Math.max(0, Math.hypot(cx - px, cy - py) - Math.max(0, r));
}

export function rectCircleOverlap(r: Rect, cx: number, cy: number, radius: number): boolean {
  const nx = clamp(cx, r.x, r.x + r.w);
  const ny = clamp(cy, r.y, r.y + r.h);
  return (cx - nx) ** 2 + (cy - ny) ** 2 <= radius * radius;
}

export function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

// ───────────────────────────── Siege Field placement ─────────────────────────────

export const SIEGE = {
  width: 34,
  height: 260,
  /** Distance in front of BASTION's centre. */
  offset: 150,
  /** Keep this far from a wall the barrier would otherwise sink into. */
  wallMargin: 30,
} as const;

/**
 * Barrier rect standing on `groundY`, `offset` px in front of the player along `dir`.
 * `wallX` (optional) is where a horizontal ray toward the spot hit a wall: the
 * barrier is pulled back so it never sinks into it (but never behind the player).
 */
export function siegeFieldRect(px: number, dir: 1 | -1, groundY: number, wallX: number | null): Rect {
  let cx = px + dir * SIEGE.offset;
  if (wallX !== null) {
    const limit = wallX - dir * (SIEGE.wallMargin + SIEGE.width / 2);
    cx = dir > 0 ? Math.min(cx, limit) : Math.max(cx, limit);
    // Never place it on the wrong side of the mech.
    cx = dir > 0 ? Math.max(cx, px + SIEGE.width / 2) : Math.min(cx, px - SIEGE.width / 2);
  }
  return { x: cx - SIEGE.width / 2, y: groundY - SIEGE.height, w: SIEGE.width, h: SIEGE.height };
}

// ───────────────────────────── Echo replay ─────────────────────────────

/**
 * Re-aim one projectile of a volley from a new origin at the same world target,
 * keeping its offset from the volley's centre angle (spreads stay spreads).
 */
export function replayAngle(specAngle: number, volleyAngle: number, fromX: number, fromY: number, target: Point): number {
  const aim = Math.atan2(target.y - fromY, target.x - fromX);
  return aim + wrapAngle(specAngle - volleyAngle);
}

/**
 * World point a volley was aimed at: the pointer when it lies roughly along the
 * shot direction, otherwise a point `fallbackDist` along the shot.
 */
export function volleyTarget(x: number, y: number, angle: number, pointer: Point | null, fallbackDist = 640): Point {
  if (pointer) {
    const dx = pointer.x - x;
    const dy = pointer.y - y;
    const d = Math.hypot(dx, dy);
    if (d > 48 && Math.abs(wrapAngle(Math.atan2(dy, dx) - angle)) < 0.6) return { x: pointer.x, y: pointer.y };
  }
  return { x: x + Math.cos(angle) * fallbackDist, y: y + Math.sin(angle) * fallbackDist };
}

// ───────────────────────────── Eclipse projectile slow ─────────────────────────────

/**
 * Eclipse keeps enemy projectiles at `factor` of their speed. Given the speed we
 * last left a projectile at (undefined = never seen) and its current speed,
 * decide whether it must be (re)scaled: new projectiles, and pooled handles
 * re-used for a fresh shot (which come back at full speed).
 */
export function needsSlowScale(storedSpeed: number | undefined, currentSpeed: number): boolean {
  if (storedSpeed === undefined) return true;
  return currentSpeed > storedSpeed * 1.5 + 30;
}

/** Uniform point in a disc from two uniform [0,1) numbers. */
export function discPoint(u: number, v: number, radius: number): Point {
  const r = Math.sqrt(u) * radius;
  const a = v * Math.PI * 2;
  return { x: Math.cos(a) * r, y: Math.sin(a) * r };
}

/** Simplify a sampled path: drop points closer than `minDist` to the previous kept one (keeps the end). */
export function simplifyPath(path: readonly Point[], minDist: number): Point[] {
  if (path.length <= 2) return path.slice();
  const out: Point[] = [path[0]];
  for (let i = 1; i < path.length - 1; i++) {
    const last = out[out.length - 1];
    if (Math.hypot(path[i].x - last.x, path[i].y - last.y) >= minDist) out.push(path[i]);
  }
  out.push(path[path.length - 1]);
  return out;
}
